import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { authApi, tenantApi } from "@/lib/api";
import type { NavigationItem } from "@/components/workspace/navigation";
import type {
    MenuPermission,
    OrderedNavigationItem,
    WorkspaceAuth,
    WorkspaceCompany,
} from "@/components/workspace/types";

const menuCache = new Map<string, MenuPermission[]>();

export const useWorkspaceLayout = () => {
    const { t } = useTranslation("global");
    const {
        user,
        setUser,
        hasTasks,
        isIt,
        isAdmin,
        isRoot,
        isDevelopment,
        isClient,
        logout,
    } = useAuth() as WorkspaceAuth;
    const [favoriteMenuIds, setFavoriteMenuIds] = useState<string[]>(() => {
        if (
            user?.config?.favorite_menus &&
            Array.isArray(user.config.favorite_menus)
        ) {
            return user.config.favorite_menus;
        }
        if (typeof window !== "undefined") {
            try {
                const saved: unknown = JSON.parse(
                    localStorage.getItem(
                        `vigepra_favorite_menus:${user?.id}`
                    ) || "[]"
                );
                return Array.isArray(saved)
                    ? saved.filter((id): id is string => typeof id === "string")
                    : [];
            } catch {
                return [];
            }
        }
        return [];
    });

    useEffect(() => {
        if (
            user?.config?.favorite_menus &&
            Array.isArray(user.config.favorite_menus)
        ) {
            setFavoriteMenuIds(user.config.favorite_menus);
            localStorage.setItem(
                `vigepra_favorite_menus:${user?.id}`,
                JSON.stringify(user.config.favorite_menus)
            );
        }
    }, [user]);

    const updateFavorites = async (newFavorites: string[]): Promise<void> => {
        setFavoriteMenuIds(newFavorites);
        localStorage.setItem(
            `vigepra_favorite_menus:${user?.id}`,
            JSON.stringify(newFavorites)
        );
        try {
            await authApi.updateConfig({ favorite_menus: newFavorites });
            if (setUser) {
                setUser((prev) => {
                    if (!prev) {
                        return prev;
                    }
                    return {
                        ...prev,
                        config: {
                            ...(prev.config || {}),
                            favorite_menus: newFavorites,
                        },
                    };
                });
            }
        } catch {
            // Keep local favorites even on network failure
        }
    };

    const toggleFavorite = (menuId: string): void => {
        const isFav = favoriteMenuIds.includes(menuId);
        const next = isFav
            ? favoriteMenuIds.filter((id) => id !== menuId)
            : [...favoriteMenuIds, menuId];

        updateFavorites(next);
    };

    const [companies, setCompanies] = useState<WorkspaceCompany[]>([]);
    const [companiesLoaded, setCompaniesLoaded] = useState(false);
    const [activeCompanyId, setActiveCompanyId] = useState<string | null>(
        () => {
            if (typeof window !== "undefined") {
                return localStorage.getItem("vigepra_active_company") || null;
            }
            return null;
        }
    );

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                if (user) {
                    const res = await tenantApi.getAll();
                    if (res.result && res.data.companies) {
                        let list: WorkspaceCompany[] = [...res.data.companies];

                        // Sort companies alphabetically
                        list.sort((a, b) =>
                            (a?.name || "").localeCompare(
                                b?.name || "",
                                undefined,
                                { sensitivity: "base" }
                            )
                        );

                        // If user is super admin or root, prepend the Principal database at the very top
                        if (
                            isAdmin ||
                            isRoot ||
                            user?.is_admin ||
                            user?.is_root
                        ) {
                            list = [
                                { id: "main", name: t("companies.main") },
                                ...list,
                            ];
                        }

                        setCompanies((previous) =>
                            list.length > 0 ? list : previous
                        );

                        if (list.length > 0) {
                            const isMatchCompany = (
                                c: WorkspaceCompany,
                                val: string | number | null | undefined
                            ): boolean =>
                                String(c.id) === String(val) ||
                                Boolean(
                                    c.database &&
                                        String(c.database) === String(val)
                                );
                            const matched = activeCompanyId
                                ? list.find((c) =>
                                      isMatchCompany(c, activeCompanyId)
                                  )
                                : null;
                            if (!matched) {
                                const defaultCompany =
                                    list.find((c) =>
                                        isMatchCompany(
                                            c,
                                            user?.default_tenant_id
                                        )
                                    ) || list[0];
                                setActiveCompanyId(String(defaultCompany.id));
                                localStorage.setItem(
                                    "vigepra_active_company",
                                    String(defaultCompany.id)
                                );
                            } else {
                                if (
                                    String(matched.id) !==
                                    String(activeCompanyId)
                                ) {
                                    setActiveCompanyId(String(matched.id));
                                    localStorage.setItem(
                                        "vigepra_active_company",
                                        String(matched.id)
                                    );
                                }
                            }
                        }
                    }
                }
            } catch {
                // error fetching companies, probably not super admin or no access
            } finally {
                setCompaniesLoaded(true);
            }
        };

        fetchCompanies();
    }, [user, isAdmin, isRoot]);

    const isPrincipalCompany = (c: WorkspaceCompany): boolean =>
        String(c.id) === "main" ||
        c.name.trim().toLowerCase() === "principal" ||
        c.name.trim().toLowerCase() === t("companies.main").toLowerCase();

    const sortedCompanies = useMemo(() => {
        if (!companies || companies.length === 0) {
            return [];
        }

        return [...companies].sort((a, b) => {
            const aPrincipal = isPrincipalCompany(a);
            const bPrincipal = isPrincipalCompany(b);
            if (aPrincipal && !bPrincipal) {
                return -1;
            } else {
                if (!aPrincipal && bPrincipal) {
                    return 1;
                }
            }
            return (a?.name || "").localeCompare(b?.name || "", undefined, {
                sensitivity: "base",
            });
        });
    }, [companies, t]);

    const activeCompany = useMemo(() => {
        if (!sortedCompanies || sortedCompanies.length === 0) {
            return null;
        }
        return (
            sortedCompanies.find(
                (c) =>
                    String(c.id) === String(activeCompanyId) ||
                    (c.database &&
                        String(c.database) === String(activeCompanyId))
            ) || sortedCompanies[0]
        );
    }, [sortedCompanies, activeCompanyId]);

    const handleCompanyChange = (companyId: string | number): void => {
        const found = companies.find(
            (c) =>
                String(c.id) === String(companyId) ||
                (c.database && String(c.database) === String(companyId))
        );
        const resolvedId = found ? String(found.id) : String(companyId);
        setActiveCompanyId(resolvedId);
        localStorage.setItem("vigepra_active_company", resolvedId);
        try {
            sessionStorage.removeItem(`vigepra_menus_${companyId}`);
            sessionStorage.removeItem(`vigepra_menus_${resolvedId}`);
            if (found && found.database) {
                sessionStorage.removeItem(`vigepra_menus_${found.database}`);
            }
        } catch {
            // Ignore storage error
        }
        window.location.href = "/";
    };

    const [isNotificationsModalOpen, setIsNotificationsModalOpen] =
        useState(false);
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(
        () => {
            try {
                const saved = localStorage.getItem("vigepra_notifications");
                if (saved) {
                    const parsed = JSON.parse(saved);
                    return Array.isArray(parsed)
                        ? parsed.filter(
                              (n: { read_at?: string | null }) => !n.read_at
                          ).length
                        : 0;
                }
                return 0;
            } catch {
                return 0;
            }
        }
    );

    const getInitials = (name?: string): string => {
        if (!name) return "??";
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const [menuPermissions, setMenuPermissions] = useState<
        MenuPermission[] | null
    >(() => {
        if (activeCompanyId && activeCompanyId !== "main") {
            if (menuCache.has(activeCompanyId)) {
                return menuCache.get(activeCompanyId) || null;
            }
            try {
                const stored = sessionStorage.getItem(
                    `vigepra_menus_${activeCompanyId}`
                );
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        menuCache.set(activeCompanyId, parsed);
                        return parsed;
                    }
                }
            } catch {
                // Ignore storage error
            }
        }
        return null;
    });

    useEffect(() => {
        const fetchMenus = () => {
            if (activeCompanyId && activeCompanyId !== "main") {
                tenantApi
                    .getMenus(activeCompanyId)
                    .then((res) => {
                        if (res.result && Array.isArray(res.data?.menus)) {
                            menuCache.set(activeCompanyId, res.data.menus);
                            try {
                                sessionStorage.setItem(
                                    `vigepra_menus_${activeCompanyId}`,
                                    JSON.stringify(res.data.menus)
                                );
                            } catch {
                                // Ignore storage error
                            }
                            setMenuPermissions(res.data.menus);
                        }
                    })
                    .catch(() => {});
            }
        };

        fetchMenus();

        const handleMenusUpdated = () => {
            fetchMenus();
        };

        window.addEventListener("menus_updated", handleMenusUpdated);
        return () => {
            window.removeEventListener("menus_updated", handleMenusUpdated);
        };
    }, [activeCompanyId]);

    const isMainCompany = activeCompanyId === "main";

    // Helper to check if a menu is allowed for current user based on fetched permissions
    const isMenuAllowed = (menuId: string): boolean => {
        if (!menuPermissions || menuPermissions.length === 0) {
            if (isAdmin || isMainCompany) {
                return true;
            }
            return false;
        }

        const config = menuPermissions.find((m) => m.tag === menuId);

        if (!config) {
            if (isAdmin || isMainCompany) {
                return true;
            }
            return false;
        }

        if (!config.is_visible) {
            return false;
        }

        if (isAdmin || isMainCompany) {
            return true;
        }

        const evaluateConfig = (cfg: MenuPermission): boolean | null => {
            const hasRoles = cfg.roles && cfg.roles.length > 0;
            if (!hasRoles) {
                return null;
            }

            if (
                user?.roles &&
                user.roles.some((r) =>
                    (cfg.roles || []).map(String).includes(String(r.id))
                )
            ) {
                return true;
            }
            return false;
        };

        // 1. Strict Parent Check: If it has a parent, the parent MUST be allowed first.
        if (config.parent) {
            const parentConfig = menuPermissions.find(
                (m) => Number(m.id) === Number(config.parent)
            );
            if (parentConfig && !parentConfig.is_visible) {
                return false;
            }

            if (parentConfig) {
                const parentAccess = evaluateConfig(parentConfig);
                if (parentAccess === false) {
                    return false;
                }
            }
        }

        const directAccess = evaluateConfig(config);

        // 2. Child evaluation
        if (directAccess === true) {
            return true;
        }

        if (directAccess === null) {
            // Inherit from parent if child has no direct restrictions
            if (config.parent) {
                const parentConfig = menuPermissions.find(
                    (m) => Number(m.id) === Number(config.parent)
                );
                if (parentConfig) {
                    return evaluateConfig(parentConfig) === true;
                }
            }
        }

        return false;
    };

    // Evaluate base visibility
    const showAdmin = isAdmin || isIt;
    const showDevelopmentsMenu = showAdmin || isDevelopment || isClient;
    const showTasksMenu = showAdmin || hasTasks;
    const showAiMenu = showAdmin;
    const showPasswordsMenu = true;
    let showTendersMenu = !isMainCompany; // Hidden in main company

    // Apply dynamic permissions for configurable menus
    if (!isMainCompany) {
        showTendersMenu =
            isMenuAllowed("header_tenders") &&
            (isMenuAllowed("clients") ||
                isMenuAllowed("tenders") ||
                isMenuAllowed("studies") ||
                isMenuAllowed("providers"));
    }

    const hasGlobalAdminMenu = menuPermissions?.some(
        (menu) =>
            menu.tag === "header_admin" && menu.is_visible !== false
    );
    const showAdminHeader = showAdmin || Boolean(hasGlobalAdminMenu);
    const showSettingsHeader =
        showPasswordsMenu ||
        showDevelopmentsMenu ||
        showTasksMenu ||
        showAiMenu;

    const navItems: NavigationItem[] = [
        ...(showAdminHeader
            ? [
                  {
                      id: "header_admin",
                      label: t("menu.admin.header"),
                      isHeader: true,
                      icon: "shield",
                  },
                  {
                      id: "companies",
                      path: "/companies",
                      label: t("menu.admin.companies"),
                      icon: "building",
                  },
                  ...(!isMainCompany
                      ? [
                            {
                                id: "menus",
                                path: "/menus",
                                label: t("menu.admin.menus"),
                                icon: "list",
                            },
                        ]
                      : []),
                  {
                      id: "users",
                      path: "/users",
                      label: t("menu.admin.users"),
                      icon: "users",
                  },
                  ...(!isMainCompany
                      ? [
                            {
                                id: "roles",
                                path: "/roles",
                                label: t("menu.admin.roles"),
                                icon: "shield",
                            },
                        ]
                      : []),
                  {
                      id: "tools",
                      path: "/tools",
                      label: t("menu.admin.tools"),
                      icon: "wrench",
                  },
              ]
            : []),

        ...(showSettingsHeader
            ? [
                  {
                      id: "header_settings",
                      label: t("menu.settings.header"),
                      isHeader: true,
                      icon: "settings",
                  },
                  ...(showPasswordsMenu
                      ? [
                            {
                                id: "passwords",
                                path: "/passwords",
                                label: t("menu.settings.passwords"),
                                icon: "key",
                            },
                        ]
                      : []),
                  ...(showDevelopmentsMenu
                      ? [
                            {
                                id: "developments",
                                path: "/developments",
                                label: t("menu.settings.developments"),
                                icon: "layers",
                            },
                        ]
                      : []),
                  ...(showTasksMenu
                      ? [
                            {
                                id: "tasks",
                                path: "/tasks",
                                label: t("menu.settings.tasks"),
                                icon: "clipboardCheck",
                            },
                        ]
                      : []),
                  ...(showAiMenu
                      ? [
                            {
                                id: "ai_consumption",
                                path: "/ai/data",
                                matchPath: "/ai",
                                label: t("menu.settings.ai_consumption"),
                                icon: "sparkles",
                            },
                        ]
                      : []),
              ]
            : []),

        ...(showTendersMenu
            ? [
                  {
                      id: "header_tenders",
                      label: t("menu.tenders.header"),
                      isHeader: true,
                      icon: "briefcase",
                  },
                  ...(isMenuAllowed("clients")
                      ? [
                            {
                                id: "clients",
                                path: "/clients",
                                label: t("menu.tenders.clients"),
                                icon: "users",
                                parent: "header_tenders",
                            },
                        ]
                      : []),
                  ...(isMenuAllowed("tenders") || isMenuAllowed("studies")
                      ? [
                            {
                                id: "tenders",
                                path: "/tenders",
                                label: t("menu.tenders.tenders"),
                                icon: "fileText",
                                parent: "header_tenders",
                            },
                        ]
                      : []),
                  ...(isMenuAllowed("providers")
                      ? [
                            {
                                id: "providers",
                                path: "/providers",
                                label: t("menu.tenders.providers"),
                                icon: "users",
                                parent: "header_tenders",
                            },
                        ]
                      : []),
              ]
            : []),
    ];

    const configurableMenuTags = [
        "header_contracting",
        "header_tenders",
        "header_purchases",
        "clients",
        "tenders",
        "studies",
        "providers",
    ];
    const globalNavItems = navItems.filter(
        (item) => !configurableMenuTags.includes(item.id)
    );
    const configuredTenantItems: NavigationItem[] =
        menuPermissions && menuPermissions.length > 0
            ? menuPermissions
                  .filter((menu) => configurableMenuTags.includes(menu.tag))
                  .filter(
                      (menu) => {
                          if (
                              menu.show_in_topbar === false ||
                              menu.is_visible === false
                          ) {
                              return false;
                          }

                          if (menu.path) {
                              return isMenuAllowed(menu.tag);
                          }

                          const children = menuPermissions.filter(
                              (child) => child.parent === menu.id
                          );

                          return (
                              isMenuAllowed(menu.tag) ||
                              children.some((child) =>
                                  isMenuAllowed(child.tag)
                              )
                          );
                      }
                  )
                  .map((menu) => {
                      const parent = menu.parent
                          ? menuPermissions.find(
                                (candidate) => candidate.id === menu.parent
                            )
                          : undefined;

                      return {
                          id: menu.tag,
                          label: menu.name || menu.tag,
                          icon: menu.icon || "circle",
                          path: menu.path || undefined,
                          parent: parent?.tag,
                          isHeader: !menu.path,
                      };
                  })
            : navItems.filter((item) => configurableMenuTags.includes(item.id));
    const configuredNavItems = [...globalNavItems, ...configuredTenantItems];

    const enrichedNavItems = configuredNavItems
        .filter((item) => {
            const config = menuPermissions?.find((menu) => menu.tag === item.id);
            const configurable = [
                "header_contracting",
                "header_tenders",
                "header_purchases",
                "clients",
                "tenders",
                "studies",
                "providers",
            ].includes(item.id);

            return !configurable || config?.show_in_topbar !== false;
        })
        .map((item, index) => {
        const p = menuPermissions?.find((p) => p.tag === item.id);
        const isConfigurable = [
            "header_contracting",
            "header_tenders",
            "header_purchases",
            "clients",
            "tenders",
            "studies",
            "providers",
        ].includes(item.id);
        const groupOrder = isConfigurable ? 2 : 1;
        const order =
            p && p.order !== undefined && p.order !== 0 ? p.order : index * 10;
        const icon = p && p.icon ? p.icon : item.icon;

        let label = item.label;
        if (p) {
            if (p.key) {
                const transKey = p.key.startsWith("menu.")
                    ? p.key
                    : `menu.${p.key}`;
                const translated = t(transKey);
                label = translated !== transKey ? translated : p.name || p.tag;
            } else if (p.name) {
                label = p.name;
            }
        }

        const path = p && p.path ? p.path : item.path;
        return {
            ...item,
            groupOrder,
            order,
            icon,
            label,
            path,
            originalIndex: index,
        };
        });

    // Recursive: group1 fixed, group2 sorted hierarchically (parent → children → grandchildren...)
    const sortHierarchically = (
        items: OrderedNavigationItem[],
        parentId: string | null = null
    ): OrderedNavigationItem[] => {
        const nodes = items
            .filter((i) => (i.parent ?? null) === parentId)
            .sort((a, b) =>
                a.order !== b.order
                    ? a.order - b.order
                    : a.originalIndex - b.originalIndex
            );
        const result: OrderedNavigationItem[] = [];
        nodes.forEach((node) => {
            result.push(node);
            sortHierarchically(items, node.id).forEach((child) =>
                result.push(child)
            );
        });
        return result;
    };

    const group1 = enrichedNavItems
        .filter((i) => i.groupOrder === 1)
        .sort((a, b) => a.order - b.order);
    const group2 = enrichedNavItems.filter((i) => i.groupOrder === 2);
    const orderedNavItems = [...group1, ...sortHierarchically(group2)];

    return {
        user,
        hasTasks,
        isIt,
        isAdmin,
        isRoot,
        isDevelopment,
        isClient,
        logout,
        companies,
        companiesLoaded,
        activeCompanyId,
        activeCompany,
        sortedCompanies,
        handleCompanyChange,
        getInitials,
        orderedNavItems,
        favoriteMenuIds,
        toggleFavorite,
        updateFavorites,
        isNotificationsModalOpen,
        setIsNotificationsModalOpen,
        unreadNotificationsCount,
        setUnreadNotificationsCount,
        isCompanyModalOpen,
        setIsCompanyModalOpen,
    };
};
